import type { User } from '@auth/domain/User';
import { useAuthState } from '@auth/hooks';
import { getAvatarColorHue, getInitials } from '@core/utils/getInitials';
import { Badge, BadgeSize, BadgeVariant, Gamerpic } from '@soteria/design-system-ve';
import { useMemo } from 'react';

import {
  accountAvatar,
  accountAvatarImage,
  accountAvatarPlaceholder,
  accountEmail,
  accountHeader,
  accountName,
  accountRoleBadge,
  accountRolesContainer,
} from './AccountTab.css';
import { settingsOptionContainer, settingsOptionLabel } from './SettingsDialog.css';

interface AccountTabProps {
  className?: string;
}

interface RolesBadgesProps {
  roles: string[];
  isSystemAdmin: boolean;
}

/**
 * Renders role badges for the user
 */
function RolesBadges({ roles, isSystemAdmin }: RolesBadgesProps) {
  const hasRoles = roles.length > 0 || isSystemAdmin;

  return (
    <div aria-labelledby="account-roles-label" className={accountRolesContainer} role="list">
      {isSystemAdmin && (
        <span role="listitem">
          <Badge className={accountRoleBadge} size={BadgeSize.Small} text="System Admin" variant={BadgeVariant.Approved} />
        </span>
      )}
      {roles.map(role => (
        <span key={role} role="listitem">
          <Badge className={accountRoleBadge} size={BadgeSize.Small} text={role} variant={BadgeVariant.Default} />
        </span>
      ))}
      {!hasRoles && <span role="listitem">No roles assigned</span>}
    </div>
  );
}

interface AccountDetailsProps {
  user: User;
  avatarBackgroundColor: string;
  isSystemAdmin: boolean;
}

/**
 * Renders the account details content
 */
function AccountDetails({ user, avatarBackgroundColor, isSystemAdmin }: AccountDetailsProps) {
  const userName = user.name || 'Unknown User';
  const userRoles = user.roles || [];

  return (
    <>
      {/* Profile Header with Avatar */}
      <div className={accountHeader}>
        <Gamerpic
          className={accountAvatar}
          gamerpicImageComponent={
            user.picture ? (
              <img alt={userName} className={accountAvatarImage} src={user.picture} />
            ) : (
              <div aria-hidden="true" className={accountAvatarPlaceholder} style={{ backgroundColor: avatarBackgroundColor }}>
                {getInitials(userName)}
              </div>
            )
          }
          imageAlt={`${userName} profile`}
        />
        <div>
          <div className={accountName}>{userName}</div>
          {user.email && <div className={accountEmail}>{user.email}</div>}
        </div>
      </div>

      {/* Name */}
      <div className={settingsOptionContainer}>
        <div className={settingsOptionLabel}>Name</div>
        <div className={settingsOptionLabel}>{userName}</div>
      </div>

      {/* Email */}
      <div className={settingsOptionContainer}>
        <div className={settingsOptionLabel}>Email</div>
        <div className={settingsOptionLabel}>{user.email || 'Not provided'}</div>
      </div>

      {/* User ID */}
      <div className={settingsOptionContainer}>
        <div className={settingsOptionLabel}>User ID</div>
        <div className={settingsOptionLabel}>{user.id}</div>
      </div>

      {/* Roles */}
      <div className={settingsOptionContainer}>
        <div className={settingsOptionLabel} id="account-roles-label">
          Roles
        </div>
        <RolesBadges isSystemAdmin={isSystemAdmin} roles={userRoles} />
      </div>
    </>
  );
}

/**
 * AccountTab component displays user account information in the Settings dialog.
 * Shows: name, email, profile picture, and roles.
 */
export function AccountTab({ className }: AccountTabProps) {
  const { user, isUserSystemAdmin } = useAuthState();

  // Compute user-specific avatar color - must be before early return
  const avatarBackgroundColor = useMemo(() => {
    const hue = getAvatarColorHue(user?.name || user?.email);

    return `hsl(${hue}, 55%, 45%)`;
  }, [user?.name, user?.email]);

  if (!user) {
    return (
      <div className={className}>
        <div className={settingsOptionContainer}>
          <div className={settingsOptionLabel}>Not signed in</div>
        </div>
      </div>
    );
  }

  return (
    <div className={className}>
      <AccountDetails avatarBackgroundColor={avatarBackgroundColor} isSystemAdmin={isUserSystemAdmin} user={user} />
    </div>
  );
}
